<template>
	<div class="products-tiles-wrap">
		<ul class="products-tiles">
			<li
				v-for="product in products"
				class="products-tiles-item"
				:class="{
					'in-cart': getProductsCountInCart(product.id),
					'has-no-image': !getProductImage(product)
				}"
				:key="product.id"
				@click="showProductDetails(product.id)"
			>
				<div class="products-tiles-holder">
					<div class="products-tiles-image-holder">
						<ProductImage
							:image-url="getProductImage(product)"
							:placeholder-url="getProductPreview(product)"
						/>

						<v-lazy-image
							v-if="!getProductImage(product)"
							class="product-image-placeholder"
							:src="getProductPlaceholder(product)"
						/>

						<div class="product-image-badge-holder">
							<ProductCart
								:cart-items-count="getProductsCountInCart(product.remoteId)"
							/>
						</div>
					</div>

					<div class="products-tiles-main-info">
						<span class="products-tiles-title">
							<span
								class="products-tiles-title-holder"
								:class="{ trending: isProductTrending(product) }"
								v-html="product.name"
							/>
						</span>

						<div class="product-tiles-holder">
							<span v-if="isProductTrending(product)" class="products-priority">
								{{ product.trendingCategoryRating }}
							</span>
							<span v-if="product.new" class="product-badge">New</span>
							<span v-if="product.favorite" class="product-badge">
								Staff Favorite
							</span>
							<span v-if="checkIsAlmostGone(product)" class="product-badge">
								Almost Gone
							</span>
							<span
								v-if="checkIsLimitedQuantity(product)"
								class="product-badge"
							>
								Limited Qty
							</span>
						</div>

						<div v-if="product.description" class="products-tiles-description">
							<v-clamp autoresize :max-lines="isOneColumn ? 3 : 2">
								{{ product.description }}
							</v-clamp>
						</div>
					</div>
				</div>
				<div class="products-tiles-price-holder">
					<div>
						<template
							v-if="
								product.promotedPrice !== null &&
									product.promotedPrice !== undefined
							"
						>
							<span class="products-tiles-price">
								<formatted-price
									:price="product.promotedPrice"
									decimal-height="70"
								/>
							</span>
							<span
								v-if="isPromotedPriceLess(product.promotedPrice, product.price)"
								class="products-tiles-alt-price alt-price"
							>
								<formatted-price :price="product.price" decimal-height="70" />
							</span>
						</template>
						<span v-else class="products-tiles-price">
							<formatted-price :price="product.price" decimal-height="70" />
						</span>
					</div>
					<div>
						<span
							v-if="checkIsSoldOut(product)"
							class="product-badge sold-badge"
						>
							Sold out
						</span>
					</div>
				</div>
			</li>
		</ul>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import VClamp from 'vue-clamp'

import ProductImage from './ProductImage'
import ProductCart from './ProductCart'

import { CLASSIFICATION, REMAINING_STATUS } from '@/constants'
import FormattedPrice from '@/components/FormattedPrice.vue'

import ProductPlaceholder from '@/assets/images/placeholder_products.svg'
import BeveragePlaceholder from '@/assets/images/placeholder_beverage2.svg'
import RetailPlaceholder from '@/assets/images/placeholder_retail.svg'
import VLazyImage from 'v-lazy-image/v2'

export default {
	name: 'ProductsTiles',

	components: {
		FormattedPrice,
		ProductImage,
		ProductCart,
		VClamp,
		VLazyImage
	},

	props: {
		products: Array,
		switcher: {
			type: String,
			default: 'regular'
		}
	},

	computed: {
		...mapState('cart', ['cartItems']),
		...mapState('ui', {
			isOneColumn: 'isMobileScreen'
		}),

		remainingStatus() {
			return REMAINING_STATUS
		}
	},

	methods: {
		isPromotedPriceLess(promotedPrice, price) {
			return Number(promotedPrice) < Number(price)
		},

		getProductsCountInCart(remoteId) {
			return this.cartItems.reduce((acc, item) => {
				if (item.remoteProductId === remoteId) {
					return acc + item.quantity
				}
				return acc
			}, 0)
		},

		getProductImage(product) {
			const productGallery = product.gallery[0]

			return productGallery && productGallery.full ? productGallery.full : ''
		},

		getProductPreview(product) {
			const productGallery = product.gallery[0]

			return productGallery && productGallery.preview
				? productGallery.preview
				: ''
		},

		getProductPlaceholder(product) {
			switch (product.classId) {
				case CLASSIFICATION.FOOD:
					return ProductPlaceholder
				case CLASSIFICATION.BEVERAGE:
					return BeveragePlaceholder
				case CLASSIFICATION.RETAIL:
					return RetailPlaceholder
				default:
					return ''
			}
		},

		checkIsAlmostGone(product) {
			return product.remainingProductStatus === this.remainingStatus.ALMOST_GONE
		},

		checkIsLimitedQuantity(product) {
			return (
				product.remainingProductStatus === this.remainingStatus.LIMITED_QUANTITY
			)
		},

		checkIsSoldOut(product) {
			const { remainingProductStatus } = product
			const { EIGHTY_SIX, LIMIT_ALMOST_REACHED } = this.remainingStatus

			return [EIGHTY_SIX, LIMIT_ALMOST_REACHED].includes(remainingProductStatus)
		},

		showProductDetails(productId) {
			this.$router.push({
				...this.$route,
				query: { ...this.$route.query, productId }
			})
		},

		isProductTrending(product) {
			return (
				product.trending &&
				product.trendingCategoryRating &&
				this.switcher === 'trending'
			)
		}
	}
}
</script>

<style lang="scss">
@import './src/assets/styles/variables';

.products-tiles-wrap {
	overflow: hidden;
}

.products-tiles {
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	margin: 0;
	padding: 0 0 17px 0;
	font-size: 11px;
	list-style: none;

	@media (min-width: 640px) {
		margin: 0 0 0 -15px;
		justify-content: flex-start;
	}

	@media (max-width: 1142px) {
		padding: 0 0 1px 0;
	}
}

.products-tiles-item {
	position: relative;
	min-height: 170px;
	width: calc(50% - 4px);
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	margin-bottom: 8px;
	cursor: pointer;
	color: $white;
	background: $deep-violet;
	padding-bottom: 24px;
	border-radius: 12px;
	border: 1px solid $border-color;
	transition: background-color 0.5s, box-shadow 0.5s, border-color 0.5s;

	@media (min-width: 414px) {
		min-height: 214px;
	}

	@media (min-width: 640px) {
		display: block;
		min-height: 350px;
		width: calc(50% - 15px);
		margin: 0 0 15px 15px;
		padding-bottom: 34px;
	}

	@media (min-width: 1024px) {
		width: calc(33.33% - 15px);

		&:hover {
			&.has-no-image {
				background-color: rgba($light-blue, 0.25);
				border-color: rgba($light-blue, 0.25);
				box-shadow: 0px 2px 7px rgba(144, 125, 213, 0.25);

				.product-image-holder {
					&::after {
						background-color: rgba($light-blue, 0.25);
					}
				}
			}

			.product-image {
				transform: scale(1.3);
			}
		}
	}

	@media (min-width: 1360px) {
		width: calc(25% - 15px);
	}

	.products-tiles-main-info {
		position: relative;
		padding: 0 10px;
		z-index: 1;

		@media (min-width: 640px) {
			margin-top: -23%;
			padding: 0 15px;
		}

		@media (min-width: 1024px) {
			margin-top: -20%;
		}
	}

	&.has-no-image {
		min-height: 170px;

		@media (min-width: 640px) {
			min-height: 350px;
		}

		.products-tiles-main-info {
			margin-top: 0;

			@media (min-width: 640px) {
				margin-top: -23%;
			}

			@media (min-width: 1024px) {
				margin-top: -20%;
			}
		}
	}

	.product-image-holder {
		position: absolute;
		top: 0;
		left: 0;
		float: none;
		width: 100%;
		height: 159px;
		border-radius: 12px 12px 0 0;
		background-image: none;
		background-color: transparent;

		@media (min-width: 640px) {
			height: 308px;
			position: relative;
		}

		&:not(.has-no-image)::after {
			content: '';
			position: absolute;
			left: 0;
			right: 0;
			bottom: -1px;
			height: 100%;
			background: linear-gradient(
				to top,
				rgba($background-popup, 1) 0%,
				transparent 100%
			);
			pointer-events: none;

			@media (min-width: 640px) {
				height: 40%;
				background: linear-gradient(
					to top,
					rgba($background-popup, 1) 0%,
					rgba($background-popup, 0.7) 20%,
					rgba($background-popup, 0.5) 40%,
					rgba($background-popup, 0.3) 60%,
					rgba($background-popup, 0.1) 80%,
					transparent 100%
				);
			}
		}
	}

	.product-badge {
		margin-bottom: 7px;
		height: 20px;
		padding: 2px 9px 5px 9px;

		@media (min-width: 640px) {
			margin-bottom: 10px;
			height: 24px;
			padding: 5px 9px 4px 9px;
		}
	}

	.products-priority {
		margin-bottom: 7px;
		height: 20px;
		background-size: 13px 11px;
		line-height: 1.5;

		@media (min-width: 640px) {
			margin-bottom: 10px;
			height: 24px;
			line-height: 2;
			background-size: 16px 15px;
		}
	}

	.products-tiles-price-holder {
		position: absolute;
		left: 10px;
		bottom: 6px;
		display: flex;
		align-items: center;

		@media (min-width: 640px) {
			bottom: 10px;
			left: 15px;
		}

		.sold-badge {
			margin-bottom: 0;
			margin-left: 10px;
			font-size: 10px;
			white-space: nowrap;
			@media (max-width: 639px) {
				padding-top: 0;
				padding-bottom: 0;
				line-height: 20px;
			}
			@media (min-width: 640px) {
				font-size: 12px;
			}
		}
	}

	.product-image-placeholder {
		position: absolute;
		top: 15px;
		left: 50%;
		opacity: 0.2;
		transform: translateX(-50%);

		@media (min-width: 640px) {
			opacity: 1;
			top: 22%;
		}
	}
}

.product-tiles-holder {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
}

.products-tiles-holder {
	position: relative;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	flex-grow: 1;
	padding-top: 45px;

	@media (min-width: 640px) {
		display: block;
		padding-top: 0;
	}
}

.products-tiles-image-holder {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;

	@media (min-width: 640px) {
		position: relative;
	}

	.product-image-badge-holder {
		left: auto;
		bottom: auto;
		right: 8px;
		top: 8px;
		transform: translateY(0);
	}
}

.products-tiles-frame {
	position: absolute;
	left: 12px;
	bottom: 12px;
	right: 12px;
	z-index: 20;
}

.products-tiles-price {
	padding: 0 5px 0 0;
	font-size: 16px;
	font-weight: 700;

	@media (min-width: 640px) {
		font-size: 20px;
	}
}

.products-tiles-alt-price {
	font-size: 16px;
	font-weight: 700;
	color: $grey;
}

.products-tiles-title {
	display: flex;
	align-items: center;
	padding: 7px 0;
	width: 100%;
	font-size: 16px;
	font-weight: 700;
	&-holder {
		word-wrap: break-word;
		max-width: 100%;
	}
	@media (min-width: 640px) {
		font-size: 20px;
		padding: 10px 0;
	}

	.highlighted-product-name {
		background-color: #816cc2;
	}
}

.product-tiles-badge-holder {
	padding: 0 0 12px;
}

.products-tiles-description {
	margin: 0 0 8px;
	font-size: 12px;
	overflow-wrap: break-word;
	word-wrap: break-word;
	word-break: normal;
	white-space: normal;
	color: $text-color;

	@media (min-width: 640px) {
		margin: 0 0 10px;
		font-size: 14px;
	}
}
</style>
